import React, { useEffect, useState } from 'react';
import { performanceMetrics } from '../../config/performance';

interface PerformanceData {
  lcp?: number;
  fid?: number;
  cls?: number;
  fcp?: number;
  ttfb?: number;
}

const PerformanceMonitor: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<PerformanceData>({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in development
    if (process.env.NODE_ENV !== 'development') return;

    // Monitor Core Web Vitals
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry;
        setPerformanceData(prev => ({ ...prev, lcp: lastEntry.startTime }));
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstEntry = entries[0] as PerformanceEventTiming;
        setPerformanceData(prev => ({ ...prev, fid: firstEntry.processingStart - firstEntry.startTime }));
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift
      const clsObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          const layoutShiftEntry = entry as LayoutShift;
          if (!layoutShiftEntry.hadRecentInput) {
            clsValue += layoutShiftEntry.value;
          }
        }
        setPerformanceData(prev => ({ ...prev, cls: clsValue }));
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });

      // First Contentful Paint
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstEntry = entries[0] as PerformanceEntry;
        setPerformanceData(prev => ({ ...prev, fcp: firstEntry.startTime }));
      });
      fcpObserver.observe({ entryTypes: ['first-contentful-paint'] });

      return () => {
        lcpObserver.disconnect();
        fidObserver.disconnect();
        clsObserver.disconnect();
        fcpObserver.disconnect();
      };
    }

    // Monitor TTFB
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry) {
      setPerformanceData(prev => ({ ...prev, ttfb: navigationEntry.responseStart - navigationEntry.requestStart }));
    }
  }, []);

  // Toggle visibility
  const toggleVisibility = () => setIsVisible(!isVisible);

  // Get performance status
  const getStatus = (metric: keyof typeof performanceMetrics.coreWebVitals, value?: number) => {
    if (!value) return 'pending';
    const threshold = performanceMetrics.coreWebVitals[metric];
    if (value <= threshold) return 'good';
    if (value <= threshold * 1.5) return 'needs-improvement';
    return 'poor';
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-600';
      case 'needs-improvement': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (process.env.NODE_ENV !== 'development') return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={toggleVisibility}
        className="bg-orange-600 text-white p-2 rounded-full shadow-lg hover:bg-orange-700 transition-colors"
        title="Performance Monitor"
      >
        📊
      </button>

      {/* Performance Panel */}
      {isVisible && (
        <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Performance Monitor</h3>
            <button
              onClick={toggleVisibility}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div className="space-y-3">
            {/* LCP */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">LCP</span>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-mono ${getStatusColor(getStatus('lcp', performanceData.lcp))}`}>
                  {performanceData.lcp ? `${Math.round(performanceData.lcp)}ms` : 'Pending'}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${getStatusColor(getStatus('lcp', performanceData.lcp))} bg-opacity-10`}>
                  {getStatus('lcp', performanceData.lcp)}
                </span>
              </div>
            </div>

            {/* FID */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">FID</span>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-mono ${getStatusColor(getStatus('fid', performanceData.fid))}`}>
                  {performanceData.fid ? `${Math.round(performanceData.fid)}ms` : 'Pending'}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${getStatusColor(getStatus('fid', performanceData.fid))} bg-opacity-10`}>
                  {getStatus('fid', performanceData.fid)}
                </span>
              </div>
            </div>

            {/* CLS */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">CLS</span>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-mono ${getStatusColor(getStatus('cls', performanceData.cls))}`}>
                  {performanceData.cls ? performanceData.cls.toFixed(3) : 'Pending'}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${getStatusColor(getStatus('cls', performanceData.cls))} bg-opacity-10`}>
                  {getStatus('cls', performanceData.cls)}
                </span>
              </div>
            </div>

            {/* FCP */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">FCP</span>
              <span className="text-sm font-mono text-gray-600">
                {performanceData.fcp ? `${Math.round(performanceData.fcp)}ms` : 'Pending'}
              </span>
            </div>

            {/* TTFB */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">TTFB</span>
              <span className="text-sm font-mono text-gray-600">
                {performanceData.ttfb ? `${Math.round(performanceData.ttfb)}ms` : 'Pending'}
              </span>
            </div>
          </div>

          {/* Thresholds Info */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500 space-y-1">
              <div>Good: LCP ≤ 2.5s, FID ≤ 100ms, CLS ≤ 0.1</div>
              <div>Needs Improvement: LCP ≤ 4s, FID ≤ 300ms, CLS ≤ 0.25</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMonitor;
